/**
 * useArtifactContextMenuForGeometry Hook
 *
 * Generic, artifact-aware context menu for Line and Shape geometries.
 * Discriminated by `artifactKind`. Default `'task'` preserves the legacy
 * behaviour for existing call sites; non-task branches are no-ops until
 * their slice + operations are wired.
 *
 * @module hooks/useArtifactContextMenuForGeometry
 */

import { useCallback, useEffect, useState } from 'react';
import { useMap } from '@vis.gl/react-maplibre';
import type { MapLayerMouseEvent } from 'maplibre-gl';

import { useGeoMarkupStore } from '@/store';
import {
  selectIsGeometryEditingActive,
  selectIsReadOnlyViewer,
} from '@/store/selectors';
import { useTaskNavigation, useTaskOperations } from '@/hooks';
import {
  openTaskForEditInline,
  openTaskForEditWithGeometry,
} from '@/utils/geometryEditHelpers';
import type { Coordinates } from '@/types';

import { useMapLayerLongPress } from './gestures';

export type ArtifactKindForGeometry = 'task' | 'obstruction';

export interface UseArtifactContextMenuForGeometryOptions {
  /** Discriminator selecting which artifact slice owns the geometry. */
  artifactKind?: ArtifactKindForGeometry;
  /** ID of the interactive (hitbox) layer registered on the MapLibre map. */
  interactiveLayerId: string;
  /** ID of the owning artifact instance (task ID / obstruction ID). */
  artifactId: string;
  /** ID of the geometry inside the artifact. */
  geometryId: string;
  /** Coordinates used for the create-here action and detail navigation. */
  coordinates: Coordinates;
}

export interface UseArtifactContextMenuForGeometryReturn {
  showDeleteDialog: boolean;
  handleShowDetails: () => void;
  handleEditArtifact: () => void;
  handleDeleteClick: () => void;
  confirmDelete: () => Promise<void>;
  cancelDelete: () => void;
  handleCreateHere: () => void;
}

/**
 * Hook order is deterministic — every render calls the same Zustand
 * subscriptions and effects regardless of `artifactKind`. The discriminator
 * only gates side-effects and returned handlers, keeping rules-of-hooks safe
 * across kind transitions.
 */
export const useArtifactContextMenuForGeometry = ({
  artifactKind = 'task',
  interactiveLayerId,
  artifactId,
  geometryId,
  coordinates,
}: UseArtifactContextMenuForGeometryOptions): UseArtifactContextMenuForGeometryReturn => {
  const isTask = artifactKind === 'task';
  const { current: map } = useMap();
  const { navigateToTaskDetails } = useTaskNavigation();
  const { createTaskAt } = useTaskOperations();

  const isGeometryEditingActive = useGeoMarkupStore(
    selectIsGeometryEditingActive
  );
  const isReadOnlyViewer = useGeoMarkupStore(selectIsReadOnlyViewer);
  const deleteTask = useGeoMarkupStore((state) => state.tasks.deleteTaskApi);
  const showContextMenu = useGeoMarkupStore(
    (state) => state.ui.showContextMenu
  );
  const hideContextMenu = useGeoMarkupStore(
    (state) => state.ui.hideContextMenu
  );

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const showTaskContextMenu = useCallback(
    (position: { x: number; y: number }) => {
      if (!isTask) return;
      // No context menu (edit/delete actions) for read-only viewers.
      // getState() snapshot — immune to stale closures if the mode flips
      // after the map listeners were registered.
      if (selectIsReadOnlyViewer(useGeoMarkupStore.getState())) return;
      showContextMenu('task', position, {
        taskId: artifactId,
        geometryId,
        coordinates,
      });
    },
    [isTask, showContextMenu, artifactId, geometryId, coordinates]
  );

  useMapLayerLongPress(interactiveLayerId, {
    // Read-only viewers never get the menu — don't even arm the timer.
    enabled: isTask && !isGeometryEditingActive && !isReadOnlyViewer,
    onLongPress: showTaskContextMenu,
  });

  const handleContextMenu = useCallback(
    (event: MapLayerMouseEvent) => {
      if (!isTask || isGeometryEditingActive) return;
      event.preventDefault();
      event.originalEvent.stopPropagation();
      showTaskContextMenu({
        x: event.originalEvent.clientX,
        y: event.originalEvent.clientY,
      });
    },
    [isTask, isGeometryEditingActive, showTaskContextMenu]
  );

  const handleShowDetails = useCallback(() => {
    if (!isTask) return;
    const task = useGeoMarkupStore
      .getState()
      .tasks.mapTasks.find((mapTask) => mapTask.id === artifactId);
    navigateToTaskDetails(task?.shortId ?? artifactId, coordinates);
    hideContextMenu();
  }, [isTask, navigateToTaskDetails, artifactId, coordinates, hideContextMenu]);

  const handleEditArtifact = useCallback(() => {
    if (!isTask) return;
    const state = useGeoMarkupStore.getState();
    openTaskForEditWithGeometry(artifactId, geometryId, coordinates, {
      allTasks: state.tasks.mapTasks,
      loadGeometries: state.loadGeometries,
      setActiveGeometry: state.setActiveGeometry,
      openTaskForEdit: openTaskForEditInline,
    });
    hideContextMenu();
  }, [isTask, artifactId, geometryId, coordinates, hideContextMenu]);

  const handleDeleteClick = useCallback(() => {
    if (!isTask) return;
    setShowDeleteDialog(true);
    hideContextMenu();
  }, [isTask, hideContextMenu]);

  const confirmDelete = useCallback(async () => {
    if (!isTask) return;
    await deleteTask(artifactId);
    setShowDeleteDialog(false);
  }, [isTask, deleteTask, artifactId]);

  const cancelDelete = useCallback(() => {
    setShowDeleteDialog(false);
  }, []);

  const handleCreateHere = useCallback(() => {
    if (!isTask) return;
    createTaskAt(coordinates);
    hideContextMenu();
  }, [isTask, createTaskAt, coordinates, hideContextMenu]);

  useEffect(() => {
    if (!map || !isTask) return;
    map.on('contextmenu', interactiveLayerId, handleContextMenu);
    return () => {
      map.off('contextmenu', interactiveLayerId, handleContextMenu);
    };
  }, [map, isTask, interactiveLayerId, handleContextMenu]);

  return {
    showDeleteDialog,
    handleShowDetails,
    handleEditArtifact,
    handleDeleteClick,
    confirmDelete,
    cancelDelete,
    handleCreateHere,
  };
};
